import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronsLeft,
  ChevronsRight,
  Keyboard,
  BadgeCheck,
  Bot,
  Flag,
  Gift,
  LifeBuoy,
  LayoutDashboard,
  MessagesSquare,
  LogOut,
  Moon,
  Search,
  ShieldCheck,
  ShieldQuestion,
  Store,
  Sun,
  Users,
  Menu as MenuIcon,
  X,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useI18n, useT } from '@/i18n'
import { LANG_LABELS, type Lang } from '@/i18n'
import { useAuth } from '@/providers/AuthProvider'
import { useTheme } from '@/providers/UiProviders'
import { PERMISSIONS, type Permission } from '@/lib/permissions'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { Button } from '@/components/ui/button'
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/menu'
import { CommandPalette } from './CommandPalette'
import { ShortcutsDialog } from './ShortcutsDialog'

/* ── Muhit ────────────────────────────────────────────────────────
   SKILL §5: prod'dan boshqa muhitda topbar bo'ylab rangli chiziq.
   Prod'da chiziq YO'Q. Bu «xato muhitda ish qilish» xatosini oldini oladi. */

export type Environment = 'prod' | 'staging' | 'dev'

export function detectEnvironment(): Environment {
  const h = window.location.hostname
  if (h === 'admin.unseenwallet.com') return 'prod'
  if (h.startsWith('staging.')) return 'staging'
  return 'dev'
}

function EnvironmentBar({ env }: { env: Environment }) {
  const t = useT()
  if (env === 'prod') return null
  const color = env === 'staging' ? 'var(--env-staging)' : 'var(--env-dev)'
  return (
    <div
      className="flex h-5 shrink-0 items-center justify-center text-xs font-semibold tracking-wide text-black uppercase"
      style={{ background: color }}
    >
      {env === 'staging' ? t.env.staging : t.env.dev}
    </div>
  )
}

/* ── Navigatsiya ──────────────────────────────────────────────── */

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  permission?: Permission
}

const SIDEBAR_KEY = 'grom_admin_sidebar'

/** Yon panel bandlari — desktop sidebar va mobil drawer BIR XIL ro'yxatdan. */
function NavItems({
  items,
  collapsed,
  onNavigate,
}: {
  items: NavItem[]
  collapsed: boolean
  onNavigate?: () => void
}) {
  return (
    <div className="flex-1 space-y-0.5 overflow-y-auto px-2 py-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          title={collapsed ? item.label : undefined}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'relative flex h-8 items-center gap-2.5 rounded-md px-2 text-base transition-colors',
              isActive
                ? 'bg-accent-subtle font-medium text-accent'
                : 'text-secondary hover:bg-surface-3 hover:text-primary',
              collapsed && 'justify-center px-0',
            )
          }
        >
          {({ isActive }) => (
            <>
              {/* Faol band ko'rsatkichi — chapdagi ingichka chiziq */}
              {isActive && !collapsed && (
                <span
                  aria-hidden
                  className="absolute top-1.5 bottom-1.5 left-0 w-0.5 rounded-full bg-accent"
                />
              )}
              <span className="shrink-0">{item.icon}</span>
              {!collapsed && <span className="truncate">{item.label}</span>}
            </>
          )}
        </NavLink>
      ))}
    </div>
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const t = useT()
  const { lang, setLang } = useI18n()
  const { theme, setTheme } = useTheme()
  const { identity, signOut, can } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 1024px)')

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1'
    } catch {
      return false
    }
  })
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const env = detectEnvironment()

  function toggleSidebar() {
    setCollapsed((v) => {
      const next = !v
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0')
      } catch {
        /* localStorage yopiq */
      }
      return next
    })
  }

  // Sahifa almashganda mobil drawer yopiladi.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  // Desktopga kengayganda drawer holati aralashmaydi.
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false)
  }, [isDesktop])

  // Drawer ochiq bo'lsa — Esc yopadi, fon skrolli qulflanadi.
  useEffect(() => {
    if (!drawerOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey)
    }
  }, [drawerOpen])

  // Global yorliqlar (SKILL §6). `g h` kabi ketma-ketliklar ham.
  useEffect(() => {
    let chord = false
    let chordTimer: number | undefined

    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null
      const typing =
        el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault()
        toggleSidebar()
        return
      }
      if (typing) return

      if (e.key === '?') {
        e.preventDefault()
        setShortcutsOpen(true)
        return
      }
      if (e.key === 'g') {
        chord = true
        window.clearTimeout(chordTimer)
        chordTimer = window.setTimeout(() => (chord = false), 1200)
        return
      }
      if (chord) {
        chord = false
        if (e.key === 'h') navigate('/')
      }
    }

    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(chordTimer)
    }
  }, [navigate])

  const items: NavItem[] = [
    { to: '/', label: t.nav.dashboard, icon: <LayoutDashboard size={15} /> },
    { to: '/users', label: t.nav.users, icon: <Users size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/groups', label: t.nav.groups, icon: <MessagesSquare size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/bots', label: t.nav.bots, icon: <Bot size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/gifts', label: t.nav.gifts, icon: <Gift size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/gromauktion', label: t.nav.gromauktion, icon: <Store size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/reports', label: t.nav.reports, icon: <Flag size={15} />, permission: PERMISSIONS.USERS_VIEW },
    {
      to: '/verification-requests',
      label: t.nav.verificationRequests,
      icon: <BadgeCheck size={15} />,
      permission: PERMISSIONS.USERS_VIEW,
    },
    { to: '/appeals', label: t.nav.appeals, icon: <ShieldQuestion size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/support', label: t.nav.support, icon: <LifeBuoy size={15} />, permission: PERMISSIONS.USERS_VIEW },
  ]

  // rbac-ui.md: sidebar bandlari huquq bo'yicha filtrlanadi.
  const visible = items.filter((i) => !i.permission || can(i.permission))

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-1">
      <EnvironmentBar env={env} />

      {/* Topbar 48px */}
      <header
        className="flex shrink-0 items-center gap-2 border-b border-border-default bg-surface-2 px-2 sm:gap-3 sm:px-3"
        style={{ height: 'var(--topbar-h)' }}
      >
        {/* Mobil: drawer ochish (desktopda sidebar doim ko'rinadi) */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={() => setDrawerOpen(true)}
          aria-label={t.nav.menu}
        >
          <MenuIcon size={16} />
        </Button>

        <div className="flex items-center gap-2">
          <span
            className="flex h-6 w-6 items-center justify-center rounded-md text-sm"
            style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
            aria-hidden
          >
            ⚡
          </span>
          <span className="hidden text-md font-semibold text-primary min-[420px]:block">
            {t.common.appName}
          </span>
        </div>

        {/* Qidiruv: keng ekranda maydon, tor ekranda belgi (ixcham) */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className={cn(
            'ml-4 hidden h-7 w-80 items-center gap-2 rounded-md border border-border-default bg-surface-1 px-2 md:flex',
            'text-sm text-tertiary transition-colors hover:border-border-strong hover:text-secondary',
          )}
        >
          <Search size={13} />
          <span className="flex-1 text-left">{t.common.search}</span>
          <kbd className="rounded-sm border border-border-default px-1 font-mono text-xs">⌘K</kbd>
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setPaletteOpen(true)}
          aria-label={t.common.search}
        >
          <Search size={15} />
        </Button>

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon"
          className="hidden sm:inline-flex"
          onClick={() => setShortcutsOpen(true)}
          title={t.shortcuts.title}
        >
          <Keyboard size={15} />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          title={t.common.theme}
        >
          {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </Button>

        <Menu
          align="end"
          width={220}
          trigger={({ toggle, ref }) => (
            <Button ref={ref} variant="ghost" size="md" onClick={toggle}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-3 text-xs">
                {identity?.username?.charAt(0).toUpperCase() ?? '?'}
              </span>
              <span className="hidden max-w-32 truncate sm:block">{identity?.username}</span>
            </Button>
          )}
        >
          {(close) => (
            <>
              <MenuLabel>{identity?.role ?? t.common.unknown}</MenuLabel>
              <MenuSeparator />
              <MenuItem
                icon={<Keyboard size={13} />}
                onSelect={() => {
                  setShortcutsOpen(true)
                  close()
                }}
              >
                {t.account.shortcuts}
              </MenuItem>
              <MenuSeparator />
              <MenuLabel>{t.common.language}</MenuLabel>
              {(Object.keys(LANG_LABELS) as Lang[]).map((l) => (
                <MenuItem
                  key={l}
                  onSelect={() => {
                    setLang(l)
                    close()
                  }}
                  trailing={lang === l ? '✓' : undefined}
                >
                  {LANG_LABELS[l]}
                </MenuItem>
              ))}
              <MenuSeparator />
              <MenuItem icon={<LogOut size={13} />} danger onSelect={signOut}>
                {t.common.signOut}
              </MenuItem>
            </>
          )}
        </Menu>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Sidebar (desktop) — holati foydalanuvchi bo'yicha saqlanadi (SKILL §5) */}
        <nav
          className="hidden shrink-0 flex-col border-r border-border-default bg-surface-2 py-2 transition-[width] duration-150 lg:flex"
          style={{ width: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)' }}
        >
          <NavItems items={visible} collapsed={collapsed} />

          <div className="px-2">
            <Button
              variant="ghost"
              size={collapsed ? 'icon' : 'md'}
              onClick={toggleSidebar}
              className={cn('w-full', collapsed && 'w-8')}
              title={collapsed ? t.nav.expand : t.nav.collapse}
            >
              {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
              {!collapsed && <span className="truncate">{t.nav.collapse}</span>}
            </Button>
          </div>
        </nav>

        {/* Kontent — kengligi CHEKLANMAYDI (SKILL §5: max-w-7xl bu yerda xato) */}
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</main>
      </div>

      {/* Mobil drawer — sidebar bilan bir xil bandlar, overlay bilan */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label={t.nav.menu}>
          <div
            className="absolute inset-0"
            style={{ background: 'var(--surface-overlay)' }}
            onClick={() => setDrawerOpen(false)}
          />
          <nav
            className="absolute inset-y-0 left-0 flex flex-col border-r border-border-default bg-surface-2 py-2"
            style={{ width: 'min(var(--sidebar-w), 85vw)', boxShadow: 'var(--shadow-panel)' }}
          >
            <div className="flex items-center justify-between px-3 pb-2">
              <div className="flex items-center gap-2">
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-md text-sm"
                  style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
                  aria-hidden
                >
                  ⚡
                </span>
                <span className="text-md font-semibold text-primary">{t.common.appName}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDrawerOpen(false)} aria-label={t.common.close}>
                <X size={15} />
              </Button>
            </div>
            <NavItems items={visible} collapsed={false} onNavigate={() => setDrawerOpen(false)} />
          </nav>
        </div>
      )}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={visible} />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  )
}

/** Sahifa sarlavhasi + amallar (56px, SKILL §5). Tor ekranda o'rab tushadi. */
export function PageHeader({
  title,
  screenId,
  description,
  actions,
  verified,
}: {
  title: string
  /** TZ ekran ID'si — ekranda ko'rinadi, qabul mezonini tekshirishni osonlashtiradi. */
  screenId: string
  description?: string
  actions?: ReactNode
  /** Jonli backend bilan tasdiqlangan ekran. */
  verified?: boolean
}) {
  return (
    <div
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border-default px-3 py-1.5"
      style={{ minHeight: 'var(--pageheader-h)' }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="truncate text-lg font-semibold text-primary">{title}</h1>
          <span
            className="rounded-sm border border-border-subtle px-1 font-mono text-xs text-tertiary"
            title="TZ ID"
          >
            {screenId}
          </span>
          {verified && <ShieldCheck size={13} className="text-success" aria-label="live" />}
        </div>
        {description && <p className="truncate text-sm text-secondary">{description}</p>}
      </div>
      <div className="flex-1" />
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
